import { create } from 'zustand';
import { en, type Dict } from './en';
import { ru } from './ru';
import { uz } from './uz';

/**
 * Tiny i18n: flat keys, `{name}` placeholders. English is the source of truth (type-checked keys);
 * other locales are partial and fall back to English. To add Russian/Uzbek, create `ru.ts`/`uz.ts`
 * exporting `Partial<Dict>` and register them in `dictionaries`.
 */
export type Locale = 'en' | 'ru' | 'uz';
export type MsgKey = keyof Dict;

const dictionaries: Record<Locale, Partial<Dict>> = { en, ru, uz };

interface LocaleState {
  locale: Locale;
  setLocale: (l: Locale) => void;
}

const stored = (() => {
  try {
    return (localStorage.getItem('cruzr.locale') as Locale | null) ?? 'en';
  } catch {
    return 'en';
  }
})();

export const useLocale = create<LocaleState>((set) => ({
  locale: stored in dictionaries ? stored : 'en',
  setLocale: (locale) => {
    try {
      localStorage.setItem('cruzr.locale', locale);
    } catch {
      /* ignore */
    }
    set({ locale });
  },
}));

export type Vars = Record<string, string | number>;

export function translate(locale: Locale, key: MsgKey, vars?: Vars): string {
  const s = dictionaries[locale][key] ?? en[key];
  if (!vars) return s;
  return s.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m));
}

/**
 * Plural form suffix for `key.one | key.few | key.many` messages (CLDR rules: ru 1 / 2–4 / 5+, en and uz 1 / other).
 * English and Uzbek define `.few` the same as `.many`.
 */
export function pluralForm(locale: Locale, n: number): 'one' | 'few' | 'many' {
  const c = new Intl.PluralRules(locale).select(n);
  return c === 'one' ? 'one' : c === 'few' ? 'few' : 'many';
}

/** Non-hook variant for toasts and callbacks. */
export function t(key: MsgKey, vars?: Vars): string {
  return translate(useLocale.getState().locale, key, vars);
}

export function useT() {
  const locale = useLocale((s) => s.locale);
  return (key: MsgKey, vars?: Vars) => translate(locale, key, vars);
}

/** Translate if `key` is a known message key, otherwise return it unchanged (server error texts). */
export function tMaybe(key: string): string {
  return key in en ? t(key as MsgKey) : key;
}
